import { useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import { Button, Modal, Typography } from "antd";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import CadenceIcon from "@/assets/icons/CadenceIcon.svg?react";
import SharedIcon from "@/assets/icons/SharedIcon.svg?react";
import InsightInformationDrawer from "../Insights/InsightInformationDrawer";
import { useAlertDeletion } from "@/hooks/useAlertDeletion";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import AlertStatusTag from "./AlertStatusTag";
import { formattedDate } from "../../utils/dateUtils";
import { getAlertDisplayCondition, getAlertStatus } from "../Insights/utils";
import { capitalizeFirst } from "@/utils/textFormattingUtils";
import type { AlertWithInsightProps } from "../Insights/insights";

const { Text, Paragraph } = Typography;

type CompactAlertCardProps = {
  alert: AlertWithInsightProps;
};

export default function CompactAlertCard({ alert }: CompactAlertCardProps) {
  const { contextHolder, deleteAlertWithFeedback } = useAlertDeletion();
  const { isSuperuserMode } = useSuperuserMode();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  if (!alert?.insight || !alert?.id) return null;

  const openDrawer = (
    e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => {
    e.stopPropagation();
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpenDeleteModal(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      openDrawer(e);
    } else if (e.key === " ") {
      e.preventDefault();
      openDrawer(e);
    }
  };

  const handleDelete = () => {
    deleteAlertWithFeedback(alert.id, () => setOpenDeleteModal(false));
  };

  const status = getAlertStatus(alert);
  const isAlerted = status === "alerted";
  const canDelete =
    !alert.insight.is_shared || (alert.insight.is_shared && isSuperuserMode);

  return (
    <>
      {contextHolder}
      <div
        className="flex items-center gap-3 mb-2 bg-white p-4 rounded-lg hover:shadow-md transition-shadow duration-200 cursor-pointer min-h-16"
        onClick={openDrawer}
        role="button"
        tabIndex={0}
        aria-label="Click to view alert details"
        onKeyDown={handleKeyDown}
        style={
          isAlerted
            ? {
                borderWidth: "1px",
                borderColor: "#EBD1E0",
              }
            : {}
        }
      >
        <AlertStatusTag status={status} />

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Text
            className="text-[#2B4752] text-base font-semibold"
            ellipsis={{
              tooltip: getAlertDisplayCondition(alert),
            }}
            style={{ margin: 0, flex: "2 1 0" }}
          >
            {getAlertDisplayCondition(alert)}
          </Text>
          <Paragraph
            className="text-[#00000073] text-sm"
            ellipsis={{
              rows: 1,
              tooltip: alert.insight.title,
            }}
            style={{ margin: 0, flex: "1 1 0" }}
          >
            {alert.insight.title}
          </Paragraph>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Text className="text-[#00000073] text-sm whitespace-nowrap">
            Updated {formattedDate(alert.updated_at, "en-US")}
          </Text>
          <div className="flex items-center gap-1">
            <CadenceIcon className="w-4 h-4" />
            <Text className="text-[#00000073] text-sm whitespace-nowrap">
              {capitalizeFirst(alert.insight.cadence)}
            </Text>
          </div>
          {alert.insight.is_shared && (
            <div className="flex items-center gap-1 text-[#00000073]">
              <SharedIcon className="w-4 h-4" />
              <Text
                className="text-[#00000073] text-sm whitespace-nowrap"
                style={{ margin: 0 }}
              >
                Shared
              </Text>
            </div>
          )}
        </div>

        {canDelete && (
          <Button
            type="text"
            className="text-[#2B4752] shrink-0"
            icon={<DeleteIcon />}
            data-testid="alert-delete-button"
            onClick={handleDeleteClick}
          />
        )}
      </div>
      <InsightInformationDrawer
        type="alert"
        insight={alert.insight}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
      <Modal
        title="Delete Alert?"
        open={openDeleteModal}
        centered
        okText="Delete"
        cancelText="Cancel"
        onCancel={() => {
          setOpenDeleteModal(false);
        }}
        footer={
          <div className="flex items-center justify-between mt-7">
            <Button
              type="default"
              className="text-[#2B4752] border border-[#2B4752] rounded-sm"
              onClick={() => setOpenDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="bg-[#980000] active:bg-[#980000]! hover:bg-[#980000]! border-0 rounded-sm"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-[#2B4752] text-base">
          Are you sure you want to delete this alert? You won&apos;t be able to
          recover it.
        </p>
      </Modal>
    </>
  );
}
